import {
  ArrowsMaximizeIcon,
  ArrowsMinimizeIcon,
  MinusIcon,
  PlusIcon,
  StackIcon,
} from '@moia-dev/pace-icons'
import { IconButton } from '@moia-dev/pace-web'
import type * as maplibregl from 'maplibre-gl'
import { type ReactNode, type RefObject, useEffect, useState } from 'react'
import { ActiveLayersDot } from './ActiveLayersDot'

type MapControlsProps = {
  /** MapLibre instance. Zoom buttons no-op until it is ready. */
  map: maplibregl.Map | null
  /**
   * Element toggled into fullscreen by the expand button — the map's positioned
   * wrapper, so the map canvas and every overlay fill the screen together.
   */
  fullscreenTargetRef: RefObject<HTMLElement | null>
  /**
   * Layers panel revealed when the layers button is toggled on (e.g. a
   * `<MapOverlayOptions />`). Omit to hide the layers button entirely for maps
   * that have no layers.
   */
  layersPanel?: ReactNode
  /**
   * Shows a badge on the layers button while any ODD layer is active, so users
   * can tell layers are on without opening the panel.
   */
  hasActiveLayers?: boolean
  /** Whether the layers panel starts open. Defaults to false. Ignored once `layersOpen` is passed. */
  defaultLayersOpen?: boolean
  /**
   * Controls the layers panel open state from outside — e.g. route-builder
   * uses this to coordinate mutual exclusion with its Annotations dropdown, so
   * opening one closes the other. Omit for the default uncontrolled behavior
   * (internal state, seeded from `defaultLayersOpen`), which is what every
   * other consumer of this rail still uses.
   */
  layersOpen?: boolean
  /** Required alongside `layersOpen` to react to the button toggling it. */
  onLayersOpenChange?: (open: boolean) => void
  /**
   * Hides the layers button while keeping `layersPanel` mounted, so a consumer
   * that must preserve layer state across navigation (route-builder) can gate
   * the button without unmounting the panel. Defaults to true.
   */
  showLayersButton?: boolean
  /**
   * Which edge the rail hugs. Defaults to `'left'` (the standard). Route-builder
   * passes `'right'` because its left edge is permanently occupied by the
   * sidebar / floating editor panel.
   */
  side?: 'left' | 'right'
  /**
   * Extra, self-contained buttons (their own trigger + dropdown, e.g. an
   * `IconButton` + `ActionMenu`) rendered beside the layers button — for a
   * control specific to one consumer rather than every map that uses this rail.
   * Route-builder passes its Annotations toggle here; every other consumer
   * omits this prop and is unaffected. Only shown alongside the layers button
   * (`showLayersButton`), since it renders in the same row.
   */
  extraButtons?: ReactNode
}

// The zoom buttons live inside a single pill, so the per-button hover fill of
// the plain IconButton looks wrong — keep the background transparent on hover.
const noHoverBackground = {
  // pace-web's plain IconButton paints its hover fill via a `.class:hover:enabled`
  // rule; match that selector (and double the `&` for specificity) so the pill's
  // buttons stay transparent on hover.
  '&&:hover:enabled': { background: 'transparent' },
} as const

/**
 * Reusable control rail shared by every admin-panel MapLibre map: an expand /
 * shrink (fullscreen) toggle and a layers toggle at the top, and zoom in / out
 * at the bottom. The layers button reveals the passed `layersPanel` and carries
 * a badge while layers are active. Hugs the left edge by default; pass
 * `side="right"` where the left edge is occupied.
 *
 * Rendered as a sibling of the map canvas inside its positioned wrapper. The
 * rail itself is click-through; only the buttons and the open panel capture
 * pointer events, so the map stays fully interactive behind them.
 */
export const MapControls = ({
  map,
  fullscreenTargetRef,
  layersPanel,
  hasActiveLayers = false,
  defaultLayersOpen = false,
  layersOpen: layersOpenProp,
  onLayersOpenChange,
  showLayersButton = true,
  side = 'left',
  extraButtons,
}: MapControlsProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [internalLayersOpen, setInternalLayersOpen] =
    useState(defaultLayersOpen)
  const isLayersOpenControlled = layersOpenProp !== undefined
  const layersOpen = isLayersOpenControlled
    ? layersOpenProp
    : internalLayersOpen
  const setLayersOpen = (next: boolean) => {
    if (!isLayersOpenControlled) setInternalLayersOpen(next)
    onLayersOpenChange?.(next)
  }
  // When the button is hidden the panel must never remain visible.
  const panelVisible = layersOpen && showLayersButton && Boolean(layersPanel)

  // Mirror the browser's fullscreen state so the expand icon stays correct even
  // when the user exits via Esc rather than the button.
  useEffect(() => {
    const syncFullscreen = () =>
      setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', syncFullscreen)
    return () =>
      document.removeEventListener('fullscreenchange', syncFullscreen)
  }, [])

  const handleExpand = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      void fullscreenTargetRef.current?.requestFullscreen?.()
    }
  }

  const handleZoomIn = () => map?.zoomIn()
  const handleZoomOut = () => map?.zoomOut()

  const isRight = side === 'right'

  return (
    <div
      css={theme => ({
        position: 'absolute',
        top: theme.space.Space4,
        left: isRight ? undefined : theme.space.Space4,
        right: isRight ? theme.space.Space4 : undefined,
        bottom: theme.space.Space4,
        zIndex: 300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isRight ? 'flex-end' : 'flex-start',
        gap: theme.space.Space2,
        // The rail spans the map's full height so the panel can grow all the
        // way to the bottom of the zoom controls. It — and every structural
        // wrapper inside it — stays click-through; only the interactive leaves
        // (buttons, panel) opt back in, so the empty areas never block the map.
        pointerEvents: 'none',
      })}
    >
      {/* Top controls sit in a row: expand alongside the layers toggle. The
          layers button and its panel share a column so the panel aligns with the
          layers icon rather than the rail edge — and the layers column is kept on
          the rail's outer edge on both sides (expand inboard) so the panel is
          flush left on the left rail and flush right on the right rail. This row
          spans the full rail height so the panel can grow all the way down to the
          bottom of the zoom controls, sitting beside them. */}
      <div
        css={theme => ({
          display: 'flex',
          // DOM order is expand-then-layers; the parent column right-aligns this
          // row on the right rail, so the layers column lands on the outer edge on
          // both sides and its panel stays flush to the rail edge.
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: theme.space.Space2,
          flex: 1,
          minHeight: 0,
        })}
      >
        <div css={{ pointerEvents: 'auto' }}>
          <IconButton
            size="compact"
            variant="secondary"
            icon={
              isFullscreen ? <ArrowsMinimizeIcon /> : <ArrowsMaximizeIcon />
            }
            label={isFullscreen ? 'Shrink map' : 'Expand map'}
            onClick={handleExpand}
          />
        </div>

        {layersPanel && showLayersButton && (
          <div
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: isRight ? 'flex-end' : 'flex-start',
              gap: theme.space.Space2,
              minHeight: 0,
              // Span the full rail height so the panel's max height reaches the
              // bottom of the zoom controls.
              maxHeight: '100%',
              height: '100%',
            })}
          >
            <div
              css={{
                position: 'relative',
                flex: '0 0 auto',
                pointerEvents: 'auto',
              }}
            >
              <IconButton
                size="compact"
                variant={layersOpen ? 'primary' : 'secondary'}
                icon={<StackIcon />}
                label={layersOpen ? 'Hide map layers' : 'Show map layers'}
                onClick={() => setLayersOpen(!layersOpen)}
              />
              {hasActiveLayers && <ActiveLayersDot />}
            </div>

            {/* Panel anchor — a click-through, zero-flow region filling the rail
                below the layers button. It gives the panel a positioning context
                and vertical span WITHOUT contributing width, so opening the panel
                never widens the layers column and never displaces the expand
                button beside it. */}
            <div
              css={{
                position: 'relative',
                flex: '1 1 auto',
                alignSelf: 'stretch',
                minHeight: 0,
                pointerEvents: 'none',
              }}
            >
              {/* Panel — absolutely positioned so it overlays the map rather than
                  pushing the sibling expand button. Anchored to the rail's outer
                  edge (right on the right rail, left on the left rail); it sizes to
                  its content and only scrolls when it would overflow the rail. */}
              <div
                css={{
                  position: 'absolute',
                  top: 0,
                  right: isRight ? 0 : undefined,
                  left: isRight ? undefined : 0,
                  maxHeight: '100%',
                  minWidth: 0,
                  overflowY: 'auto',
                  display: panelVisible ? 'flex' : 'none',
                  pointerEvents: 'auto',
                }}
              >
                {layersPanel}
              </div>
            </div>
          </div>
        )}

        {/* Consumer-specific button(s) beside the layers icon (e.g.
            route-builder's Annotations toggle). Self-contained — each manages
            its own open state and dropdown — so this rail only needs to give it
            a slot in the row, not any panel-anchoring logic. */}
        {extraButtons && showLayersButton && (
          <div css={{ pointerEvents: 'auto' }}>{extraButtons}</div>
        )}
      </div>

      {/* Zoom controls pinned to the bottom rail corner, out of the top row's
          flow, so the layers panel can extend down to their bottom edge. The +
          and − share a single white pill split by a thin divider (matching the
          here-maps ZoomButtons), rather than two detached buttons. */}
      <div
        css={theme => ({
          position: 'absolute',
          bottom: 0,
          left: isRight ? undefined : 0,
          right: isRight ? 0 : undefined,
          display: 'flex',
          flexDirection: 'column',
          background: theme.semantic.ColorSurfaceDefault,
          borderRadius: theme.radius.RadiusMedium,
          outline: `1px solid ${theme.semantic.ColorBorderOnActionSecondaryDefault}`,
          boxShadow: 'var(--shadow-depth-1)',
          overflow: 'hidden',
          pointerEvents: 'auto',
        })}
      >
        <IconButton
          size="compact"
          variant="plain"
          icon={<PlusIcon />}
          label="Zoom in"
          onClick={handleZoomIn}
          css={noHoverBackground}
        />
        <div
          aria-hidden
          css={theme => ({
            height: 1,
            margin: `0 ${theme.space.Space1}`,
            backgroundColor: theme.semantic.ColorBorderOnActionSecondaryDefault,
          })}
        />
        <IconButton
          size="compact"
          variant="plain"
          icon={<MinusIcon />}
          label="Zoom out"
          onClick={handleZoomOut}
          css={noHoverBackground}
        />
      </div>
    </div>
  )
}
